import { User } from "lucide-react";
import { type QuizConfig } from "~/types/quiz";

// Standard 5-point Likert scale options for Big 5 personality test
const BIG5_OPTIONS = [
  "Uopšte se ne slažem",
  "Uglavnom se ne slažem",
  "Ni slažem se ni ne slažem se",
  "Uglavnom se slažem",
  "Potpuno se slažem",
];

// Big 5 personality test based on IPIP-NEO-PI-R 120 questions
export const big5Personality: QuizConfig = {
  id: "big-5-personality",
  illustration: {
    src: "/images/quizzes/big-5-personality.webp",
    alt: "Žena sa sveskom posmatra prijatelje u parku.",
  },
  lastModified: "2026-07-31",
  title: "Big 5 Test Ličnosti",
  shortTitle: "Big 5 Ličnosti",
  description:
    "Otkrij svoje glavne karakteristike ličnosti kroz naučno potvrđen test koji meri pet osnovnih dimenzija ličnosti.",
  longDescription:
    "Besplatan Big 5 test ličnosti zasnovan na IPIP-NEO modelu sa 121 pitanja. Meri pet ključnih dimenzija: otvorenost, savesnost, ekstraverziju, saradljivost i neuroticizam. Dobijaš detaljan profil sa skorovima i personalizovanim uvidima o tome kako tvoja ličnost utiče na odnose, karijeru i svakodnevni život.",
  duration: "15-20 min",
  icon: User,
  category: "Ličnost i emocije",
  focusAreasWanted: [
    "self_development",
    "identity",
    "emotional_regulation",
    "relationships",
  ],
  promptExtension: `
    - When presenting scores, show them as "X/100" format (e.g., "75/100 na Neuroticizmu")
    - Use clear section headings with emoji markers for each Big 5 dimension
    - Format the feedback in a structured way with clear visual breaks
    - Focus on practical implications of each score in everyday life
    - Balance between strengths and growth areas
    - Show examples of how the score can be used to improve life
    - Show examples of how the score affects their life both positively and negatively
  `,
  questions: [
    // All 120 questions from IPIP-NEO-PI-R with Serbian translations
    {
      type: "radio",
      question: "'Brinem se o stvarima' - koliko se slažeš sa ovim?",
      options: BIG5_OPTIONS,
      scores: [1, 2, 3, 4, 5], // plus keyed - N1
    },
    {
      type: "radio",
      question: "Lako stičem prijatelje",
      options: BIG5_OPTIONS,
      scores: [1, 2, 3, 4, 5], // plus keyed - E1
    },
    {
      type: "radio",
      question: "Imam živu maštu",
      options: BIG5_OPTIONS,
      scores: [1, 2, 3, 4, 5], // plus keyed - O1
    },
    {
      type: "radio",
      question: "Verujem drugima",
      options: BIG5_OPTIONS,
      scores: [1, 2, 3, 4, 5], // plus keyed - A1
    },
    {
      type: "radio",
      question: "Uspešno završavam zadatke",
      options: BIG5_OPTIONS,
      scores: [1, 2, 3, 4, 5], // plus keyed - C1
    },
    {
      type: "radio",
      question: "Lako se naljutim",
      options: BIG5_OPTIONS,
      scores: [1, 2, 3, 4, 5], // plus keyed - N2
    },
    {
      type: "radio",
      question: "Volim velike zabave",
      options: BIG5_OPTIONS,
      scores: [1, 2, 3, 4, 5], // plus keyed - E2
    },
    {
      type: "radio",
      question: "Verujem u značaj umetnosti",
      options: BIG5_OPTIONS,
      scores: [1, 2, 3, 4, 5], // plus keyed - O2
    },
    {
      type: "radio",
      question: "Koristim druge u svoje svrhe",
      options: BIG5_OPTIONS,
      scores: [5, 4, 3, 2, 1], // minus keyed - A2
    },
    {
      type: "radio",
      question: "Volim da sredim stvari",
      options: BIG5_OPTIONS,
      scores: [1, 2, 3, 4, 5], // plus keyed - C2
    },
    {
      type: "radio",
      question: "Često se osećam tužno",
      options: BIG5_OPTIONS,
      scores: [1, 2, 3, 4, 5], // plus keyed - N3
    },
    {
      type: "radio",
      question: "Preuzimam kontrolu",
      options: BIG5_OPTIONS,
      scores: [1, 2, 3, 4, 5], // plus keyed - E3
    },
    {
      type: "radio",
      question: "Intenzivno doživljavam svoje emocije",
      options: BIG5_OPTIONS,
      scores: [1, 2, 3, 4, 5], // plus keyed - O3
    },
    {
      type: "radio",
      question: "Volim da pomažem drugima",
      options: BIG5_OPTIONS,
      scores: [1, 2, 3, 4, 5], // plus keyed - A3
    },
    {
      type: "radio",
      question: "Držim svoja obećanja",
      options: BIG5_OPTIONS,
      scores: [1, 2, 3, 4, 5], // plus keyed - C3
    },
    {
      type: "radio",
      question: "Teško mi je da pristupim drugima",
      options: BIG5_OPTIONS,
      scores: [1, 2, 3, 4, 5], // plus keyed - N4
    },
    {
      type: "radio",
      question: "Uvek sam zauzet/a",
      options: BIG5_OPTIONS,
      scores: [1, 2, 3, 4, 5], // plus keyed - E4
    },
    {
      type: "radio",
      question: "Preferiram raznolikost umesto rutine",
      options: BIG5_OPTIONS,
      scores: [1, 2, 3, 4, 5], // plus keyed - O4
    },
    {
      type: "radio",
      question: "Volim dobru svađu",
      options: BIG5_OPTIONS,
      scores: [5, 4, 3, 2, 1], // minus keyed - A4
    },
    {
      type: "radio",
      question: "Naporno radim",
      options: BIG5_OPTIONS,
      scores: [1, 2, 3, 4, 5], // plus keyed - C4
    },
    {
      type: "radio",
      question: "Pijem/jedem bez mere",
      options: BIG5_OPTIONS,
      scores: [1, 2, 3, 4, 5], // plus keyed - N5
    },
    {
      type: "radio",
      question: "Volim uzbuđenje",
      options: BIG5_OPTIONS,
      scores: [1, 2, 3, 4, 5], // plus keyed - E5
    },
    {
      type: "radio",
      question: "Volim da čitam izazovan materijal",
      options: BIG5_OPTIONS,
      scores: [1, 2, 3, 4, 5], // plus keyed - O5
    },
    {
      type: "radio",
      question: "Verujem da sam bolji/a od drugih",
      options: BIG5_OPTIONS,
      scores: [5, 4, 3, 2, 1], // minus keyed - A5
    },
    {
      type: "radio",
      question: "Uvek sam spreman/na",
      options: BIG5_OPTIONS,
      scores: [1, 2, 3, 4, 5], // plus keyed - C5
    },
    {
      type: "radio",
      question: "Lako paničim",
      options: BIG5_OPTIONS,
      scores: [1, 2, 3, 4, 5], // plus keyed - N6
    },
    {
      type: "radio",
      question: "Zračim radošću",
      options: BIG5_OPTIONS,
      scores: [1, 2, 3, 4, 5], // plus keyed - E6
    },
    {
      type: "radio",
      question: "Skloniji sam da glasam za liberalne kandidate",
      options: BIG5_OPTIONS,
      scores: [1, 2, 3, 4, 5], // plus keyed - O6
    },
    {
      type: "radio",
      question: "Saosećam sa beskućnicima",
      options: BIG5_OPTIONS,
      scores: [1, 2, 3, 4, 5], // plus keyed - A6
    },
    {
      type: "radio",
      question: "Skačem u stvari ne razmišljajući",
      options: BIG5_OPTIONS,
      scores: [5, 4, 3, 2, 1], // minus keyed - C6
    },
    {
      type: "radio",
      question: "Strahuju da će se desiti najgore",
      options: BIG5_OPTIONS,
      scores: [1, 2, 3, 4, 5], // plus keyed - N1
    },
    {
      type: "radio",
      question: "Osećam se prijatno oko ljudi",
      options: BIG5_OPTIONS,
      scores: [1, 2, 3, 4, 5], // plus keyed - E1
    },
    {
      type: "radio",
      question: "Uživam u divljim letovima fantazije",
      options: BIG5_OPTIONS,
      scores: [1, 2, 3, 4, 5], // plus keyed - O1
    },
    {
      type: "radio",
      question: "Verujem da drugi imaju dobre namere",
      options: BIG5_OPTIONS,
      scores: [1, 2, 3, 4, 5], // plus keyed - A1
    },
    {
      type: "radio",
      question: "Odličan/na sam u onome što radim",
      options: BIG5_OPTIONS,
      scores: [1, 2, 3, 4, 5], // plus keyed - C1
    },
    {
      type: "radio",
      question: "Lako se iznerviram",
      options: BIG5_OPTIONS,
      scores: [1, 2, 3, 4, 5], // plus keyed - N2
    },
    {
      type: "radio",
      question: "Razgovaram sa puno različitih ljudi na zabavama",
      options: BIG5_OPTIONS,
      scores: [1, 2, 3, 4, 5], // plus keyed - E2
    },
    {
      type: "radio",
      question: "Vidim lepotu u stvarima koje drugi možda ne primećuju",
      options: BIG5_OPTIONS,
      scores: [1, 2, 3, 4, 5], // plus keyed - O2
    },
    {
      type: "radio",
      question: "Varam da bih napredovao/la",
      options: BIG5_OPTIONS,
      scores: [5, 4, 3, 2, 1], // minus keyed - A2
    },
    {
      type: "radio",
      question: "Često zaboravim da vratim stvari na svoje mesto",
      options: BIG5_OPTIONS,
      scores: [5, 4, 3, 2, 1], // minus keyed - C2
    },
    {
      type: "radio",
      question: "Ne volim sebe",
      options: BIG5_OPTIONS,
      scores: [1, 2, 3, 4, 5], // plus keyed - N3
    },
    {
      type: "radio",
      question: "Pokušavam da vodim druge",
      options: BIG5_OPTIONS,
      scores: [1, 2, 3, 4, 5], // plus keyed - E3
    },
    {
      type: "radio",
      question: "Osećam emocije drugih",
      options: BIG5_OPTIONS,
      scores: [1, 2, 3, 4, 5], // plus keyed - O3
    },
    {
      type: "radio",
      question: "Brinem se za druge",
      options: BIG5_OPTIONS,
      scores: [1, 2, 3, 4, 5], // plus keyed - A3
    },
    {
      type: "radio",
      question: "Govorim istinu",
      options: BIG5_OPTIONS,
      scores: [1, 2, 3, 4, 5], // plus keyed - C3
    },
    {
      type: "radio",
      question: "Bojim se da privučem pažnju na sebe",
      options: BIG5_OPTIONS,
      scores: [1, 2, 3, 4, 5], // plus keyed - N4
    },
    {
      type: "radio",
      question: "Uvek sam u pokretu",
      options: BIG5_OPTIONS,
      scores: [1, 2, 3, 4, 5], // plus keyed - E4
    },
    {
      type: "radio",
      question: "Preferiram da se držim stvari koje znam",
      options: BIG5_OPTIONS,
      scores: [5, 4, 3, 2, 1], // minus keyed - O4
    },
    {
      type: "radio",
      question: "Vičem na ljude",
      options: BIG5_OPTIONS,
      scores: [5, 4, 3, 2, 1], // minus keyed - A4
    },
    {
      type: "radio",
      question: "Radim više nego što se od mene očekuje",
      options: BIG5_OPTIONS,
      scores: [1, 2, 3, 4, 5], // plus keyed - C4
    },
    {
      type: "radio",
      question: "Retko preterujem",
      options: BIG5_OPTIONS,
      scores: [5, 4, 3, 2, 1], // minus keyed - N5
    },
    {
      type: "radio",
      question: "Tražim avanturu",
      options: BIG5_OPTIONS,
      scores: [1, 2, 3, 4, 5], // plus keyed - E5
    },
    {
      type: "radio",
      question: "Izbegavam filozofske diskusije",
      options: BIG5_OPTIONS,
      scores: [5, 4, 3, 2, 1], // minus keyed - O5
    },
    {
      type: "radio",
      question: "Visoko mislim o sebi",
      options: BIG5_OPTIONS,
      scores: [5, 4, 3, 2, 1], // minus keyed - A5
    },
    {
      type: "radio",
      question: "Sprovodim svoje planove",
      options: BIG5_OPTIONS,
      scores: [1, 2, 3, 4, 5], // plus keyed - C5
    },
    {
      type: "radio",
      question: "Postanем preopterećen/a događajima",
      options: BIG5_OPTIONS,
      scores: [1, 2, 3, 4, 5], // plus keyed - N6
    },
    {
      type: "radio",
      question: "Puno se zabavljam",
      options: BIG5_OPTIONS,
      scores: [1, 2, 3, 4, 5], // plus keyed - E6
    },
    {
      type: "radio",
      question: "Verujem da ne postoji apsolutno dobro i zlo",
      options: BIG5_OPTIONS,
      scores: [1, 2, 3, 4, 5], // plus keyed - O6
    },
    {
      type: "radio",
      question: "Saosećam sa onima kojima je gore od mene",
      options: BIG5_OPTIONS,
      scores: [1, 2, 3, 4, 5], // plus keyed - A6
    },
    {
      type: "radio",
      question: "Donosim nepromišljene odluke",
      options: BIG5_OPTIONS,
      scores: [5, 4, 3, 2, 1], // minus keyed - C6
    },
    {
      type: "radio",
      question: "Bojim se mnogo stvari",
      options: BIG5_OPTIONS,
      scores: [1, 2, 3, 4, 5], // plus keyed - N1
    },
    {
      type: "radio",
      question: "Izbegavam kontakte sa drugima",
      options: BIG5_OPTIONS,
      scores: [5, 4, 3, 2, 1], // minus keyed - E1
    },
    {
      type: "radio",
      question: "Volim da sanjarim",
      options: BIG5_OPTIONS,
      scores: [1, 2, 3, 4, 5], // plus keyed - O1
    },
    {
      type: "radio",
      question: "Verujem onome što ljudi kažu",
      options: BIG5_OPTIONS,
      scores: [1, 2, 3, 4, 5], // plus keyed - A1
    },
    {
      type: "radio",
      question: "Glatko rešavam zadatke",
      options: BIG5_OPTIONS,
      scores: [1, 2, 3, 4, 5], // plus keyed - C1
    },
    {
      type: "radio",
      question: "Gubim živce",
      options: BIG5_OPTIONS,
      scores: [1, 2, 3, 4, 5], // plus keyed - N2
    },
    {
      type: "radio",
      question: "Preferiram da budem sam/a",
      options: BIG5_OPTIONS,
      scores: [5, 4, 3, 2, 1], // minus keyed - E2
    },
    {
      type: "radio",
      question: "Ne volim poeziju",
      options: BIG5_OPTIONS,
      scores: [5, 4, 3, 2, 1], // minus keyed - O2
    },
    {
      type: "radio",
      question: "Iskorišćavam druge",
      options: BIG5_OPTIONS,
      scores: [5, 4, 3, 2, 1], // minus keyed - A2
    },
    {
      type: "radio",
      question: "Ostavljam nered u svojoj sobi",
      options: BIG5_OPTIONS,
      scores: [5, 4, 3, 2, 1], // minus keyed - C2
    },
    {
      type: "radio",
      question: "Često sam potišten/a",
      options: BIG5_OPTIONS,
      scores: [1, 2, 3, 4, 5], // plus keyed - N3
    },
    {
      type: "radio",
      question: "Preuzimam kontrolu nad stvarima",
      options: BIG5_OPTIONS,
      scores: [1, 2, 3, 4, 5], // plus keyed - E3
    },
    {
      type: "radio",
      question: "Retko primećujem svoje emocionalne reakcije",
      options: BIG5_OPTIONS,
      scores: [5, 4, 3, 2, 1], // minus keyed - O3
    },
    {
      type: "radio",
      question: "Ravnodušan/na sam prema osećanjima drugih",
      options: BIG5_OPTIONS,
      scores: [5, 4, 3, 2, 1], // minus keyed - A3
    },
    {
      type: "radio",
      question: "Kršim pravila",
      options: BIG5_OPTIONS,
      scores: [5, 4, 3, 2, 1], // minus keyed - C3
    },
    {
      type: "radio",
      question: "Osećam se prijatno samo sa prijateljima",
      options: BIG5_OPTIONS,
      scores: [1, 2, 3, 4, 5], // plus keyed - N4
    },
    {
      type: "radio",
      question: "Puno radim u slobodno vreme",
      options: BIG5_OPTIONS,
      scores: [1, 2, 3, 4, 5], // plus keyed - E4
    },
    {
      type: "radio",
      question: "Ne volim promene",
      options: BIG5_OPTIONS,
      scores: [5, 4, 3, 2, 1], // minus keyed - O4
    },
    {
      type: "radio",
      question: "Vređam ljude",
      options: BIG5_OPTIONS,
      scores: [5, 4, 3, 2, 1], // minus keyed - A4
    },
    {
      type: "radio",
      question: "Radim tačno onoliko koliko je potrebno da prođem",
      options: BIG5_OPTIONS,
      scores: [5, 4, 3, 2, 1], // minus keyed - C4
    },
    {
      type: "radio",
      question: "Lako odolevam iskušenjima",
      options: BIG5_OPTIONS,
      scores: [5, 4, 3, 2, 1], // minus keyed - N5
    },
    {
      type: "radio",
      question: "Uživam da budem bezobziran/na",
      options: BIG5_OPTIONS,
      scores: [1, 2, 3, 4, 5], // plus keyed - E5
    },
    {
      type: "radio",
      question: "Teško razumem apstraktne ideje",
      options: BIG5_OPTIONS,
      scores: [5, 4, 3, 2, 1], // minus keyed - O5
    },
    {
      type: "radio",
      question: "Imam visoko mišljenje o sebi",
      options: BIG5_OPTIONS,
      scores: [5, 4, 3, 2, 1], // minus keyed - A5
    },
    {
      type: "radio",
      question: "Gubim vreme",
      options: BIG5_OPTIONS,
      scores: [5, 4, 3, 2, 1], // minus keyed - C5
    },
    {
      type: "radio",
      question: "Osećam da ne mogu da se nosim sa stvarima",
      options: BIG5_OPTIONS,
      scores: [1, 2, 3, 4, 5], // plus keyed - N6
    },
    {
      type: "radio",
      question: "Volim život",
      options: BIG5_OPTIONS,
      scores: [1, 2, 3, 4, 5], // plus keyed - E6
    },
    {
      type: "radio",
      question: "Skloniji sam da glasam za konzervativne kandidate",
      options: BIG5_OPTIONS,
      scores: [5, 4, 3, 2, 1], // minus keyed - O6
    },
    {
      type: "radio",
      question: "Ne zanimaju me tuđi problemi",
      options: BIG5_OPTIONS,
      scores: [5, 4, 3, 2, 1], // minus keyed - A6
    },
    {
      type: "radio",
      question: "Jurišam u stvari",
      options: BIG5_OPTIONS,
      scores: [5, 4, 3, 2, 1], // minus keyed - C6
    },
    {
      type: "radio",
      question: "Lako se streširam",
      options: BIG5_OPTIONS,
      scores: [1, 2, 3, 4, 5], // plus keyed - N1
    },
    {
      type: "radio",
      question: "Držim druge na distanci",
      options: BIG5_OPTIONS,
      scores: [5, 4, 3, 2, 1], // minus keyed - E1
    },
    {
      type: "radio",
      question: "Volim da se izgubim u mislima",
      options: BIG5_OPTIONS,
      scores: [1, 2, 3, 4, 5], // plus keyed - O1
    },
    {
      type: "radio",
      question: "Ne verujem ljudima",
      options: BIG5_OPTIONS,
      scores: [5, 4, 3, 2, 1], // minus keyed - A1
    },
    {
      type: "radio",
      question: "Znam kako da obavim stvari",
      options: BIG5_OPTIONS,
      scores: [1, 2, 3, 4, 5], // plus keyed - C1
    },
    {
      type: "radio",
      question: "Nisu me lako iznervirati",
      options: BIG5_OPTIONS,
      scores: [5, 4, 3, 2, 1], // minus keyed - N2
    },
    {
      type: "radio",
      question: "Izbegavam gužve",
      options: BIG5_OPTIONS,
      scores: [5, 4, 3, 2, 1], // minus keyed - E2
    },
    {
      type: "radio",
      question: "Ne uživam u odlasku u muzeje umetnosti",
      options: BIG5_OPTIONS,
      scores: [5, 4, 3, 2, 1], // minus keyed - O2
    },
    {
      type: "radio",
      question: "Ometam planove drugih",
      options: BIG5_OPTIONS,
      scores: [5, 4, 3, 2, 1], // minus keyed - A2
    },
    {
      type: "radio",
      question: "Ostavljam svoje stvari svuda",
      options: BIG5_OPTIONS,
      scores: [5, 4, 3, 2, 1], // minus keyed - C2
    },
    {
      type: "radio",
      question: "Osećam se prijatno sa sobom",
      options: BIG5_OPTIONS,
      scores: [5, 4, 3, 2, 1], // minus keyed - N3
    },
    {
      type: "radio",
      question: "Čekam da drugi povuku prvi korak",
      options: BIG5_OPTIONS,
      scores: [5, 4, 3, 2, 1], // minus keyed - E3
    },
    {
      type: "radio",
      question: "Ne razumem ljude koji postaju emotivni",
      options: BIG5_OPTIONS,
      scores: [5, 4, 3, 2, 1], // minus keyed - O3
    },
    {
      type: "radio",
      question: "Ne odvajam vreme za druge",
      options: BIG5_OPTIONS,
      scores: [5, 4, 3, 2, 1], // minus keyed - A3
    },
    {
      type: "radio",
      question: "Kršim svoja obećanja",
      options: BIG5_OPTIONS,
      scores: [5, 4, 3, 2, 1], // minus keyed - C3
    },
    {
      type: "radio",
      question: "Ne uznemiravaju me teške socijalne situacije",
      options: BIG5_OPTIONS,
      scores: [5, 4, 3, 2, 1], // minus keyed - N4
    },
    {
      type: "radio",
      question: "Volim da se opustim",
      options: BIG5_OPTIONS,
      scores: [5, 4, 3, 2, 1], // minus keyed - E4
    },
    {
      type: "radio",
      question: "Vezan/a sam za konvencionalne načine",
      options: BIG5_OPTIONS,
      scores: [5, 4, 3, 2, 1], // minus keyed - O4
    },
    {
      type: "radio",
      question: "Vraćam drugima",
      options: BIG5_OPTIONS,
      scores: [5, 4, 3, 2, 1], // minus keyed - A4
    },
    {
      type: "radio",
      question: "Uložim malo vremena i truda u svoj posao",
      options: BIG5_OPTIONS,
      scores: [5, 4, 3, 2, 1], // minus keyed - C4
    },
    {
      type: "radio",
      question: "Mogu da kontrolišem svoje žudnje",
      options: BIG5_OPTIONS,
      scores: [5, 4, 3, 2, 1], // minus keyed - N5
    },
    {
      type: "radio",
      question: "Ponašam se divlje i ludo",
      options: BIG5_OPTIONS,
      scores: [1, 2, 3, 4, 5], // plus keyed - E5
    },
    {
      type: "radio",
      question: "Ne zanimaju me teoretske diskusije",
      options: BIG5_OPTIONS,
      scores: [5, 4, 3, 2, 1], // minus keyed - O5
    },
    {
      type: "radio",
      question: "Hvalim se svojim vrlinama",
      options: BIG5_OPTIONS,
      scores: [5, 4, 3, 2, 1], // minus keyed - A5
    },
    {
      type: "radio",
      question: "Teško mi je da počnem zadatke",
      options: BIG5_OPTIONS,
      scores: [5, 4, 3, 2, 1], // minus keyed - C5
    },
    {
      type: "radio",
      question: "Ostajem smiren/a pod pritiskom",
      options: BIG5_OPTIONS,
      scores: [5, 4, 3, 2, 1], // minus keyed - N6
    },
    {
      type: "radio",
      question: "Gledam na svetlu stranu života",
      options: BIG5_OPTIONS,
      scores: [1, 2, 3, 4, 5], // plus keyed - E6
    },
    {
      type: "radio",
      question: "Verujem da treba biti strog prema kriminalcima",
      options: BIG5_OPTIONS,
      scores: [5, 4, 3, 2, 1], // minus keyed - O6
    },
    {
      type: "radio",
      question: "Pokušavam da ne mislim o potrebitima",
      options: BIG5_OPTIONS,
      scores: [5, 4, 3, 2, 1], // minus keyed - A6
    },
    {
      type: "radio",
      question: "Delam bez razmišljanja",
      options: BIG5_OPTIONS,
      scores: [5, 4, 3, 2, 1], // minus keyed - C6
    },
    {
      type: "textarea",
      question:
        "Opiši osobinu svoje ličnosti koja najviše utiče na tvoje odnose ili posao:",
    },
  ],
  copy: {
    title: "Tvoj Big 5 profil ličnosti",
    subtitle:
      "Otkrij svoje glavne karakteristike ličnosti kroz pet osnovnih dimenzija",
    scoreLabel: "Tvoji rezultati po dimenzijama ličnosti:",
    adviceTitle: "Uvidi o tvojoj ličnosti",
    resourcesTitle: "Preporučujemo da pročitaš:",
    whatNextTitle: "Šta dalje?",
    shareButtonText: "Podeli rezultat",
  },
  landing: {
    summary:
      "Big 5 model je najšire prihvaćen naučni okvir za razumevanje ličnosti. Ovaj test meri pet ključnih dimenzija: otvorenost za iskustva, savesnost, ekstraverziju, saradljivost i neuroticizam. Sa 121 pitanja zasnovanih na IPIP-NEO modelu, dobijaš detaljan profil koji otkriva kako tvoja ličnost utiče na odnose, posao i svakodnevne odluke.",
    highlights: [
      {
        title: "121 pitanja, 5 dimenzija",
        description:
          "Najdetaljniji besplatni Big 5 test na srpskom, zasnovan na IPIP-NEO modelu.",
      },
      {
        title: "Naučno potvrđen model",
        description:
          "Big 5 je zlatni standard u psihologiji ličnosti sa decenijama istraživanja.",
      },
      {
        title: "Personalizovani uvidi",
        description:
          "AI analizira tvoje odgovore i daje konkretne savete za lični razvoj.",
      },
    ],
    whoIsItFor:
      "Za svakoga ko želi da dublje razume sebe. Posebno koristan ako razmišljaš o karijernom usmerenju, želiš da poboljšaš odnose sa drugima ili te zanima kako tvoja ličnost utiče na tvoje svakodnevne odluke.",
    whatYouLearn:
      "Saznaćeš koliko si otvoren/a za nova iskustva, koliko si organizovan/a i disciplinovan/a, da li si više introvert ili ekstrovert, koliko si saradljiv/a i kako reaguješ na stres. Svaka dimenzija dolazi sa praktičnim savetima.",
    howItWorks:
      "Test sadrži 120 tvrdnji na koje odgovaraš na skali od 1 do 5. Na osnovu odgovora, AI generiše tvoj personalizovani profil sa skorovima za svaku dimenziju i konkretnim uvidima.",
    nextSteps:
      "Nakon testa, možeš da razgovaraš sa terapeutom o tome kako da iskoristiš jake strane svoje ličnosti i radiš na oblastima koje želiš da razviješ.",
    faq: [
      {
        question: "Šta je Big 5 test ličnosti?",
        answer:
          "Big 5 (ili petofaktorski model) je najšire prihvaćen naučni model za opisivanje ličnosti. Meri pet osnovnih dimenzija: otvorenost, savesnost, ekstraverziju, saradljivost i neuroticizam. Koristi se u psihologiji, HR-u i ličnom razvoju širom sveta.",
      },
      {
        question: "Koliko je Big 5 test pouzdan?",
        answer:
          "Big 5 model ima odličnu pouzdanost potvrđenu u hiljadama studija. Ovaj test koristi IPIP-NEO pitanja koja su validirana na velikim uzorcima. Rezultati su stabilni tokom vremena, ali se mogu menjati sa životnim iskustvima.",
      },
      {
        question: "Ko treba da uradi Big 5 test?",
        answer:
          "Svako ko želi da bolje razume sebe. Test je posebno koristan za ljude koji biraju karijeru, žele da poboljšaju komunikaciju sa partnerom ili kolegama, ili su u procesu psihoterapije. Nema starosnog ograničenja za odrasle.",
      },
      {
        question: "Šta znače rezultati Big 5 testa?",
        answer:
          "Rezultati pokazuju tvoj skor na svakoj od pet dimenzija. Nema 'dobrih' ili 'loših' rezultata. Svaka kombinacija ima svoje prednosti i izazove. Na primer, visok neuroticizam znači veću emotivnu osetljivost, ali i dublji emotivni doživljaj.",
      },
      {
        question: "Da li ovaj test zamenjuje razgovor sa psihologom?",
        answer:
          "Ne. Test je korisna polazna tačka za samoupoznavanje, ali ne zamenjuje profesionalnu procenu. Za dublje razumevanje svoje ličnosti ili rad na specifičnim temama, preporučujemo razgovor sa licenciranim terapeutom.",
      },
    ],
    ctaLabel: "Započni Big 5 test ličnosti",
  },
};
